<template>
  <div>
    <safe-teleport to="#toolbar-portal">
      <q-toolbar
        class="q-px-sm row justify-center"
        :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
        style="height: 75px"
        :style="`border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
      >
        <div class="property-compare full-width row">
          <q-input
            v-if="isEditMode"
            v-model="reportCopy.name"
            filled
            style="font-size: 20px; font-weight: 500"
            placeholder="Enter a name"
            dense
            clearable
          />
          <div v-else style="font-size: 20px; font-weight: 500" class="q-mr-sm q-my-auto">
            New Property Report
          </div>
          <q-select
            v-if="isEditMode && $q.screen.gt.sm"
            v-model="reportCopy.tags"
            class="q-ml-sm"
            transition-duration="100"
            :options="availableReportTags"
            :color="reportCopy.tags.length > 1 ? 'primary' : 'accent'"
            dense
            options-dense
            outlined
            multiple
            style="width: 160px; font-size: 12px"
          >
            <template #prepend>
              <q-icon name="label" size="14px" />
            </template>
            <template #selected>
              <div style="font-size: 11px" class="text-weight-regular text-elipsis">
                {{ reportCopy.tags.join(', ') }}
              </div>
            </template>
          </q-select>
          <q-space />
          <div class="q-gutter-x-sm q-my-auto">
            <q-btn
              v-if="isEditMode"
              color="red"
              outline
              :loading="deleteLoading"
              :dense="$q.screen.lt.sm"
              no-caps
              flat
              @click="deleteDialog = true"
            >
              <span v-if="$q.screen.gt.sm"> Delete </span>
              <q-icon v-else name="delete" size="medium" />
            </q-btn>
            <q-btn outline no-caps :dense="$q.screen.lt.sm" flat @click="cancel">
              <span v-if="$q.screen.gt.sm"> Cancel </span>
              <q-icon v-else name="close" size="medium" />
            </q-btn>
            <q-btn
              id="intercom-generate-report"
              no-caps
              unelevated
              color="accent"
              :loading="isSubmitting"
              :disable="!canSubmit"
              :dense="$q.screen.lt.sm"
              @click="submit('top-button')"
            >
              <span v-if="$q.screen.gt.sm">
                {{ isEditMode ? 'Save Report' : 'Generate Report' }}
              </span>
              <q-icon v-else name="save" size="medium" />
            </q-btn>
          </div>
        </div>
      </q-toolbar>
    </safe-teleport>
    <div v-if="reportCopy" class="property-compare q-gutter-y-lg q-mx-auto">
      <enter-addresses
        :report="reportCopy"
        @update-property-details="updatePropertyDetails"
        @update-report="updateReport"
        @new-custom-property="safelyNavigate"
      />

      <email-notifications
        v-if="reportCopy.comparable_addresses.length || reportCopy.subject"
        :emails="reportCopy.emails"
        :schedule="reportCopy.schedule"
        @update-emails="updateEmails"
        @update-schedule="updateSchedule"
      />
    </div>
    <q-dialog v-model="displayReportNameDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section>
          <div class="text-h6">Name your report</div>
        </q-card-section>
        <q-card-section class="q-pt-none">
          <q-input
            v-model="reportCopy.name"
            placeholder="Enter a name"
            dense
            autofocus
            @keyup.enter="submit()"
          />
          <q-select
            v-model="reportCopy.tags"
            transition-duration="100"
            label="Tag"
            :options="availableReportTags"
            dense
            multiple
            outlined
            class="q-mt-md"
            style="width: 200px"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup no-caps unelevated flat label="Cancel" />
          <q-btn
            id="intercom-generate-report-save"
            no-caps
            unelevated
            color="primary"
            @click="submit()"
            >Save</q-btn
          >
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-model="deleteDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <span class="q-ml-sm">
            Are you sure you want to permanently delete <b>{{ reportCopy.name }}</b
            >?
          </span>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            no-caps
            unelevated
            label="Delete"
            color="red"
            :loading="deleteLoading"
            @click="$emit('delete')"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { toRaw, type PropType } from 'vue'
import type { RentSourceSubscriptionAttributes } from 'common'
import EnterAddresses from './EnterAddresses.vue'
import EmailNotifications from '@/components/EmailNotifications.vue'
import { useUserStore } from '@/stores/user'
import { computePropertyEdits, deconstructPropertyEdits } from '@/utils'
import type { RouteLocation } from 'vue-router'

export default {
  name: 'PropertyReportSettings',
  components: {
    EmailNotifications,
    EnterAddresses
  },
  props: {
    report: {
      type: Object as PropType<RentSourceSubscriptionAttributes>,
      required: true
    }
  },
  emits: ['submit', 'delete', 'update-property-details', 'cancel'],
  data() {
    return {
      reportCopy: this.report,
      deleteDialog: false,
      deleteLoading: false,
      displayReportNameDialog: false,
      isSubmitting: false,
      manualPropertyEdits: {} as Record<string, Record<string, string | number>>
    }
  },
  computed: {
    isEditMode() {
      return this.report.id !== -1
    },
    availableReportTags(): string[] {
      return useUserStore().availableReportTags
    },
    canSubmit() {
      return this.reportCopy.comparable_addresses.length > 0 || this.reportCopy.subject !== null
    },
    propertyEdits() {
      return computePropertyEdits(this.manualPropertyEdits)
    }
  },
  created() {
    // impossible to copy a reactive object, so we need to clone it
    this.reportCopy = structuredClone(toRaw(this.report))
  },
  methods: {
    cancel() {
      // we call the cancel event instead of using a router-link so that
      // the parent component can handle the navigation and display a
      // dialog to confirm the user wants to leave the page if needed
      if (this.isEditMode) {
        this.$emit('cancel', { name: 'Property Report', params: { reportId: this.report.id } })
      } else {
        this.$emit('cancel', { name: 'Dashboard' })
      }
    },
    safelyNavigate(to: RouteLocation) {
      this.$emit('cancel', to)
    },
    updateReport(report: RentSourceSubscriptionAttributes) {
      this.reportCopy = report
    },
    updateEmails(emails: string[]) {
      this.reportCopy.emails = emails
    },
    updateSchedule(schedule: string[]) {
      this.reportCopy.schedule = schedule
    },
    updatePropertyDetails(propertyId: string, key: string, value: string | number) {
      if (!this.manualPropertyEdits[propertyId]) {
        this.manualPropertyEdits[propertyId] = {}
      }
      this.manualPropertyEdits[propertyId][key] = value
      this.$emit('update-property-details', deconstructPropertyEdits(this.propertyEdits))
    },
    submit(buttonClicked?: 'top-button' | 'bottom-button') {
      if (!this.reportCopy.name && !this.report.name) {
        // we initialize the report name with the subject building name or street address
        if (this.reportCopy.subject) {
          this.reportCopy.name =
            this.reportCopy.subject.building_name || this.reportCopy.subject.street_address
        } else {
          this.reportCopy.name =
            [...new Set(this.reportCopy.comparable_addresses.map((_) => _.city))]
              .sort()
              .join('-')
              .split(' ')
              .join('-') + ' Property Report'
        }

        // we display the dialog to allow the user to change the report name
        this.displayReportNameDialog = true
        return
      }

      this.displayReportNameDialog = false
      this.reportCopy.property_edits = this.propertyEdits
      this.isSubmitting = true
      // TODO: remove this tracking event when experiment is over (start: 2024-06-21)
      if (buttonClicked) {
        this.$tracker.generateReportButtonClicked(buttonClicked)
      }
      this.$emit('submit', this.reportCopy)
    }
  }
}
</script>
<style scoped>
.text-elipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.property-compare {
  max-width: 1600px;
}
</style>
